import React, { FC, ReactElement, useContext } from 'react';
import { Query } from '@apollo/client/react/components';
import classNames from 'classnames';
import moment from 'moment';

import Attachments from './ShowPage/Attachments';
import AuthorDisplay from 'buyside/components/presentation/WhitePaper/ShowPage/AuthorDisplay';
import CommentList from 'buyside/components/presentation/WhitePaper/Comments/WhitePaperCommentList';
import { defaultUserImageUrl } from 'buyside/util/urlHelper';
import Details from './ShowPage/Details';
import LoadingSpinner, {
  SIZES
} from 'buyside/components/presentation/LoadingSpinner/loadingSpinner';
import MentionedEntities from 'buyside/components/presentation/WhitePaper/ShowPage/MentionedEntities';
import Thesis from './ShowPage/Thesis';
import {
  WhitePaperShowPageQuery,
  WhitePaperShowPageQueryVariables
} from 'interfaces/graphql-typings';
import WhitePaperEditButton from 'buyside/components/presentation/WhitePaper/ShowPage/EditReportButton/WhitePaperEditReportButton';
import WhitePaperShowPageCategories from 'buyside/components/presentation/WhitePaper/ShowPage/Categories/WhitePaperShowPageCategories';
import WhitePaperShowPageRelatedResearch from 'buyside/components/presentation/WhitePaper/ShowPage/RelatedResearch/WhitePaperShowPageRelatedResearch';
import WhitePaperStatusBarDraft from 'buyside/components/presentation/WhitePaper/ShowPage/StatusBar/Draft/WhitePaperStatusBarDraft';
import YouTubePlayer from 'buyside/components/presentation/YouTubePlayer/youtubePlayer';
import { NavigationContext } from 'buyside/components/presentation/MainSiteLayout/mainSiteLayout';

const { whitePaperStatuses } = require('buyside/util/constantsFromRails.json');

const WhitePaperShowPage = require('buyside/graphql/queries/WhitePaperShowPage.graphql');

import styles from './styles.scss';

const Container: FC<{ id: string }> = ({ id }): ReactElement => {
  const { sideNavigationOpen } = useContext(NavigationContext) || {};

  return (
    <section>
      <Query<WhitePaperShowPageQuery, WhitePaperShowPageQueryVariables>
        query={WhitePaperShowPage}
        variables={{ id }}
      >
        {({ loading, data, error }): ReactElement => {
          if (loading) return <LoadingSpinner size={SIZES.LARGE} />;
          if (error) throw error;
          if (!data?.whitePaper) throw new Error();

          const {
            associatedEmployer,
            elevatorPitch,
            esgs = [],
            geographies = [],
            globalMacros = [],
            mentionedEntities,
            pdfThesisUrl,
            positionDisclaimerFund,
            positionDisclaimerIndividual,
            sectors = [],
            status,
            submittedAt,
            tagList: hashTags,
            thesis,
            title,
            updatedAt,
            user,
            whitePaperAttachments,
            youtubeIds
          } = data.whitePaper;

          const secondaryResearch =
            whitePaperAttachments
              ?.filter(({ researchType }) => researchType === 'secondary')
              ?.map(({ id, filename, createdAt, attachmentType, fileType }) => ({
                id,
                filename,
                createdAt,
                attachmentType,
                fileType
              })) || [];
          const financialModelResearch =
            whitePaperAttachments
              ?.filter(({ researchType }) => researchType === 'financial_model')
              ?.map(({ id, filename, createdAt, attachmentType, fileType }) => ({
                id,
                filename,
                createdAt,
                attachmentType,
                fileType
              })) || [];

          const authorId = user?.id;
          const activeFollowersCount = user?.activeFollowersCount;
          const displayName = user?.displayName;
          const featuredRank = user?.featuredRank?.rankString || null;
          const profileCity = user?.profileCity || null;
          const profileImageUrl = user?.profileImageUrl || defaultUserImageUrl();

          const mostRecentEmployer = user?.mostRecentEmployer;
          const isCurrentEmployer = Boolean(mostRecentEmployer?.currentEmployer);
          const jobTitle = mostRecentEmployer?.title;

          const {
            aumRange,
            fundType,
            id: employerId,
            name: employerName,
            employerProfile: fundProfile
          } = associatedEmployer || mostRecentEmployer?.employer || {};

          const fundFollowerCount = fundProfile?.followerCount;
          const fundProfileId = fundProfile?.id;

          const currentUser = data.currentUser;
          const isAuthor = user.id === currentUser?.id;
          const canWriteWhitePaperComments = Boolean(currentUser?.canWriteWhitePaperComments);
          const isAdmin = Boolean(currentUser?.isAdmin);
          const canCommentAnonymously = Boolean(currentUser?.canCommentAnonymously);
          const canViewUserDetails = Boolean(currentUser?.canViewUserDetails);

          return (
            <div className={styles.container}>
              {status === whitePaperStatuses.draft && <WhitePaperStatusBarDraft id={id} />}
              <section
                className={classNames(styles.twoColContainer, {
                  [styles.sideNavigationOpen]: Boolean(sideNavigationOpen)
                })}
              >
                <div className={styles.leftColumn}>
                  <div>
                    <Details
                      hashTags={hashTags}
                      updatedAt={moment(new Date(updatedAt || '')).format('MM/DD/YYYY')}
                      submittedAt={moment(new Date(submittedAt || '')).format('MM/DD/YYYY')}
                      elevatorPitch={elevatorPitch}
                      title={title || ''}
                    />
                    {(isAuthor || isAdmin) && <WhitePaperEditButton id={id} />}
                    <AuthorDisplay
                      id={authorId}
                      displayName={displayName}
                      featuredRank={featuredRank}
                      employerId={employerId || ''}
                      employerName={employerName || ''}
                      fundFollowerCount={fundFollowerCount || 0}
                      hasAssociatedEmployer={Boolean(associatedEmployer)}
                      hasFundProfile={Boolean(fundProfile)}
                      isCurrentEmployer={isCurrentEmployer}
                      aumRange={aumRange || ''}
                      fundType={fundType || ''}
                      profileCity={profileCity}
                      profileImageUrl={profileImageUrl}
                      title={jobTitle || ''}
                      fundProfileId={fundProfileId || ''}
                      activeFollowersCount={activeFollowersCount}
                      isAuthor={currentUser?.id === authorId}
                      showUserDetails={canViewUserDetails}
                    />
                  </div>
                  <div className={styles.mobileColumn}>
                    <WhitePaperShowPageCategories
                      sectors={sectors}
                      geographies={geographies}
                      esgs={esgs}
                      globalMacros={globalMacros}
                    />
                    {Boolean(whitePaperAttachments?.length) && (
                      <Attachments
                        secondaryResearch={secondaryResearch}
                        financialModelResearch={financialModelResearch}
                        isLocked={false}
                      />
                    )}
                    {mentionedEntities && Boolean(mentionedEntities.length) && (
                      <MentionedEntities mentionedEntities={mentionedEntities} />
                    )}
                    <WhitePaperShowPageRelatedResearch
                      id={id}
                      canViewUserDetails={canViewUserDetails}
                    />
                    {youtubeIds &&
                      youtubeIds.map(
                        (id: string): ReactElement => (
                          <YouTubePlayer key={id} width={489.6} height={298.35} videoId={id} />
                        )
                      )}
                  </div>
                  <Thesis
                    thesis={thesis}
                    pdfThesisUrl={pdfThesisUrl}
                    positionDisclaimerFund={Boolean(positionDisclaimerFund)}
                    positionDisclaimerIndividual={Boolean(positionDisclaimerIndividual)}
                  />
                  {status === whitePaperStatuses.open && (
                    <CommentList
                      authorId={authorId}
                      authorName={displayName}
                      canCommentAnonymously={Boolean(canCommentAnonymously)}
                      canWriteWhitePaperComments={Boolean(canWriteWhitePaperComments)}
                      isAdmin={Boolean(isAdmin)}
                      isAuthor={Boolean(isAuthor)}
                      isEmployed={Boolean(isCurrentEmployer)}
                      profileImageUrl={profileImageUrl}
                      whitePaperId={id}
                    />
                  )}
                </div>
                <div className={styles.rightColumn}>
                  <WhitePaperShowPageCategories
                    sectors={sectors}
                    geographies={geographies}
                    esgs={esgs}
                    globalMacros={globalMacros}
                  />
                  {Boolean(whitePaperAttachments?.length) && (
                    <Attachments
                      secondaryResearch={secondaryResearch}
                      financialModelResearch={financialModelResearch}
                      isLocked={false}
                    />
                  )}
                  {mentionedEntities && Boolean(mentionedEntities.length) && (
                    <MentionedEntities mentionedEntities={mentionedEntities} />
                  )}
                  <WhitePaperShowPageRelatedResearch
                    id={id}
                    canViewUserDetails={canViewUserDetails}
                  />
                  {youtubeIds &&
                    youtubeIds.map(
                      (id: string): ReactElement => (
                        <YouTubePlayer key={id} width={345.6} height={210.6} videoId={id} />
                      )
                    )}
                </div>
              </section>
            </div>
          );
        }}
      </Query>
    </section>
  );
};

export default Container;
